"use client";

import { createContext, type ReactNode, useContext, useRef } from "react";
import { type StoreApi, useStore } from "zustand";
import {
  type AddonsConfigStore,
  createAddonsConfigStore,
} from "@/features/addons/store/addons-config-store";
import type { ConfigurationItemAddon } from "@/features/addons/types/configuration-item-addon.type";

const AddonsConfigContext = createContext<StoreApi<AddonsConfigStore> | null>(
  null,
);

export const AddonsConfigProvider = ({
  children,
  store,
  initialAddons = [],
}: {
  children: ReactNode;
  // Share a store instance created by the parent (so it can read selections on
  // save while the modal writes them). Falls back to an internally created one.
  store?: StoreApi<AddonsConfigStore>;
  initialAddons?: ConfigurationItemAddon[];
}) => {
  // A shared store is followed as it is passed: the configuration view swaps it
  // for a fresh one when the configured diet changes, and children must see the
  // new selection instead of the previous diet's one (MPL-3851).
  const fallbackStoreRef = useRef<StoreApi<AddonsConfigStore>>(undefined);
  if (!fallbackStoreRef.current) {
    fallbackStoreRef.current = createAddonsConfigStore(initialAddons);
  }

  return (
    <AddonsConfigContext.Provider value={store ?? fallbackStoreRef.current}>
      {children}
    </AddonsConfigContext.Provider>
  );
};

export const useAddonsConfigStore = <T,>(
  selector: (store: AddonsConfigStore) => T,
): T => {
  const context = useContext(AddonsConfigContext);

  if (!context) {
    throw new Error(
      "useAddonsConfigStore must be used within AddonsConfigProvider",
    );
  }

  return useStore(context, selector);
};
